import { useRef, useCallback } from "react";

interface UseCodeSuggestionOptions {
  onCodeSuggested: (code: string) => void;
  enabled?: boolean;
}

/**
 * Converts a name to a lowercase_snake_case code.
 */
const generateCodeFromName = (name: string): string => {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .replace(/_+/g, "_");
};

/**
 * Hook that auto-suggests a code based on a name input.
 * Only triggers suggestion when the name changes and the code field
 * hasn't been manually edited by the user.
 */
export function useCodeSuggestion({
  onCodeSuggested,
  enabled = true,
}: UseCodeSuggestionOptions) {
  const hasUserEditedCode = useRef(false);

  const handleNameChange = useCallback(
    (name: string) => {
      if (!enabled || !name.trim() || hasUserEditedCode.current) {
        return;
      }

      const code = generateCodeFromName(name);
      if (code) {
        onCodeSuggested(code);
      }
    },
    [enabled, onCodeSuggested]
  );

  const handleCodeChange = useCallback(() => {
    hasUserEditedCode.current = true;
  }, []);

  const resetUserEdited = useCallback(() => {
    hasUserEditedCode.current = false;
  }, []);

  return {
    handleNameChange,
    handleCodeChange,
    resetUserEdited,
  };
}
